import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  BULK_ORDER_GRID_AXES,
  getCartLineKey,
  removeBulkOrderCartLine,
  resolveGridSide,
  selectGridContext,
  selectIsCartEditable,
  toStoredCartPowers,
  updateCartLineQuantity,
  upsertBulkOrderCartLine,
} from '../../common/bulkOrder'

const useBulkOrderGridQuantities = () => {
  const dispatch = useDispatch()
  const selectedLensId = useSelector(state => state.bulkOrder.selectedLensId)
  const lensDetail = useSelector(state => state.bulkOrder.lensDetail)
  const gridContext = useSelector(selectGridContext)
  const cartLines = useSelector(state => state.bulkOrder.cart.lines)
  const isCartEditable = useSelector(selectIsCartEditable)

  // Assign after commit so a discarded concurrent render cannot leak into
  // an event from the previous tree. Keep [dispatch] as the only callback dep.
  const lookupRef = useRef({
    selectedLensId,
    lensDetail,
    gridContext,
    cartLines,
    isCartEditable,
  })
  useEffect(() => {
    lookupRef.current = {
      selectedLensId,
      lensDetail,
      gridContext,
      cartLines,
      isCartEditable,
    }
  }, [selectedLensId, lensDetail, gridContext, cartLines, isCartEditable])

  const handleQuantityChange = useCallback(
    ({ sphere, cylinder, addition = 0, quantity }) => {
      // Read latest lookup via ref so this callback identity stays stable
      // across cart line edits (memoized grid cells depend on it).
      const {
        selectedLensId: lensId,
        lensDetail: detail,
        gridContext: context,
        cartLines: lines,
        isCartEditable: editable,
      } = lookupRef.current

      if (!editable) return
      if (!lensId || !detail || context.diameter == null) return
      if (String(detail._id) !== String(lensId)) return

      const gridAxis = detail.gridAxis || BULK_ORDER_GRID_AXES.CYLINDER
      const side = resolveGridSide(
        detail.hasSideSelection,
        context.side,
      )
      const stored = toStoredCartPowers(
        sphere,
        gridAxis === BULK_ORDER_GRID_AXES.ADDITION ? addition : cylinder,
        gridAxis,
        context.cylinderSign,
      )

      const lineKey = getCartLineKey({
        lensId,
        diameter: context.diameter,
        side,
        sphere: stored.sphere,
        cylinder: stored.cylinder,
        addition: stored.addition,
      })
      const line = lines.find(item => getCartLineKey(item) === lineKey)

      if (quantity === 0) {
        if (line) {
          dispatch(removeBulkOrderCartLine({ id: line.id }))
        }
        return
      }

      if (line) {
        dispatch(updateCartLineQuantity({ id: line.id, quantity }))
        return
      }

      dispatch(
        upsertBulkOrderCartLine({
          lensId,
          lensCode: detail.code,
          lensName: detail.name,
          diameter: context.diameter,
          side,
          sphere: stored.sphere,
          cylinder: stored.cylinder,
          addition: stored.addition,
          gridAxis,
          quantity,
        }),
      )
    },
    [dispatch],
  )

  return { handleQuantityChange }
}

export default useBulkOrderGridQuantities
